import React from 'react';
import { motion } from 'framer-motion';
import phoneMockup from '../../assets/images/revoai-mockup.png';

const RevoAIPage = () => {
    const features = [
        { title: 'Virtual Transformation', text: 'Transform any photo into stunning virtual designs in seconds.', icon: '🎨' },
        { title: 'Fill Empty Spaces', text: 'AI automatically fills empty rooms with furniture and decor.', icon: '🪑' },
        { title: 'Furniture Replacement', text: 'Replace existing furniture with new styles instantly.', icon: '🛋️' },
        { title: 'Landscape Design', text: 'Redesign outdoor spaces, gardens, and landscapes with AI.', icon: '🌳' },
    ];

    return (
        <div className="min-h-screen bg-dark pt-24 pb-16">
            {/* Background orbs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-accent-purple opacity-15 rounded-full blur-3xl" />
                <div className="absolute top-60 right-20 w-96 h-96 bg-accent-orange opacity-10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-accent-pink opacity-10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-6xl mx-auto px-6 relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="flex flex-col lg:flex-row items-center gap-12 mb-16"
                >
                    {/* Left Content */}
                    <div className="flex-1 text-center lg:text-left">
                        <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="inline-block px-4 py-2 rounded-full bg-dark-card border border-accent-orange text-sm font-custom2 text-accent-orange mb-4"
                        >
                            NEW
                        </motion.span>

                        <h1 className="text-4xl md:text-5xl font-custom1 text-white mb-2">
                            RevoAI
                        </h1>
                        <span className="bg-gradient-to-r from-accent-purple to-accent-orange bg-clip-text text-transparent font-custom2 text-xl block mb-6">
                            Transform Your Space with AI
                        </span>

                        <p className="text-lg font-custom2 text-gray-400 mb-8 leading-relaxed">
                            Instantly redesign rooms, transform landscapes, fill empty spaces, and replace furniture using advanced AI technology. Turn your vision into reality.
                        </p>

                        {/* Store Badges */}
                        <div className="flex gap-4 justify-center lg:justify-start">
                            <a
                                href="https://play.google.com/store/apps/details?id=io.interiorai.home.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-dark-card border border-gray-600 px-5 py-3 rounded-xl hover:border-accent-orange transition-colors duration-300"
                            >
                                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                                </svg>
                                <span className="font-custom2 text-sm text-white">Play Store</span>
                            </a>
                        </div>
                    </div>

                    {/* Right - Phone Mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex-1 flex justify-center"
                    >
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-accent-purple to-accent-orange opacity-20 blur-3xl rounded-full scale-110" />
                            <img
                                src={phoneMockup}
                                alt="RevoAI app screen"
                                className="relative w-72 md:w-96 h-auto rounded-2xl drop-shadow-2xl"
                            />
                        </div>
                    </motion.div>
                </motion.div>

                {/* Features */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mb-16"
                >
                    <h2 className="text-2xl font-custom1 text-white mb-8 text-center">Features</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {features.map((feature, idx) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }}
                                className="bg-dark-card border border-gray-600 rounded-2xl p-6 hover:border-accent-purple transition-colors duration-300 flex items-start gap-4"
                            >
                                <span className="text-3xl">{feature.icon}</span>
                                <div>
                                    <h3 className="text-lg font-custom1 text-white mb-2">{feature.title}</h3>
                                    <p className="text-sm font-custom2 text-gray-400">{feature.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Policies */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="text-center mb-10"
                >
                    <p className="text-gray-500 font-custom2 text-sm">
                        By using RevoAI, you agree to our{' '}
                        <a href="/revoai/privacy-policy" className="text-accent-purple hover:underline">Privacy Policy</a>,{' '}
                        <a href="/revoai/terms-and-conditions" className="text-accent-purple hover:underline">Terms & Conditions</a>, and{' '}
                        <a href="/revoai/eula" className="text-accent-purple hover:underline">EULA</a>.
                    </p>
                </motion.div>

                {/* Footer */}
                <div className="text-center">
                    <p className="text-gray-600 font-custom2 text-sm">© 2024 RevoAI – All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default RevoAIPage;
